<template>
  <div class="admin-container">
    <div class="admin-content">
      <h1 class="admin-title">Admin Panel</h1>

      <!-- Tab Navigation -->
      <div class="tabs-container">
        <button @click="activeTab = 'users'" :class="['tab-button', { active: activeTab === 'users' }]">All Users</button>
        <button @click="activeTab = 'studies'" :class="['tab-button', { active: activeTab === 'studies' }]">All Studies</button>
        <button @click="activeTab = 'researchers'" :class="['tab-button', { active: activeTab === 'researchers' }]">Researchers</button>
        <button @click="activeTab = 'patientsummaries'" :class="['tab-button', { active: activeTab === 'patientsummaries' }]">Patient Summaries</button>
        <button @click="activeTab = 'clinicaltrials'" :class="['tab-button', { active: activeTab === 'clinicaltrials' }]">ClinicalTrials.gov</button>
        <button @click="activeTab = 'auditlogs'" :class="['tab-button', { active: activeTab === 'auditlogs' }]">Audit Logs</button>
      </div>
      <!-- ClinicalTrials.gov Section -->
      <ClinicalTrialsManagement v-if="activeTab === 'clinicaltrials'" @study-imported="onStudyImported" />

      <!-- Audit Logs Section -->
      <AuditLogsManagement v-if="activeTab === 'auditlogs'" />

      <!-- Users List Section -->
      <UsersManagement 
        v-if="activeTab === 'users'" 
        :currentUserId="currentUserId"
        :isAuthenticated="isAuthenticated"
      />

      <!-- Studies List Section -->
      <StudiesManagement
        v-if="activeTab === 'studies'"
        ref="studiesManagement"
        :isAuthenticated="isAuthenticated"
        :isAdmin="isAdmin"
      />

      <!-- Researchers Section -->
      <div v-if="activeTab === 'researchers'" class="admin-section">
        <ResearcherManagement />
      </div>

      <!-- Patient Summaries Section -->
      <PatientSummariesManagement
        v-if="activeTab === 'patientsummaries'"
        :isAuthenticated="isAuthenticated"
        :isAdmin="isAdmin"
      />
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { API_ENDPOINTS, HTTP_STATUS, ROUTES, ERROR_MESSAGES } from '@/constants'
import ResearcherManagement from './admin/ResearcherManagement.vue'
import ClinicalTrialsManagement from './admin/ClinicalTrialsManagement.vue'
import UsersManagement from './admin/UsersManagement.vue'
import StudiesManagement from './admin/StudiesManagement.vue'
import AuditLogsManagement from './admin/AuditLogsManagement.vue'
import PatientSummariesManagement from './admin/PatientSummariesManagement.vue'

export default {
  name: 'Admin',
  components: {
    ResearcherManagement,
    ClinicalTrialsManagement,
    UsersManagement,
    StudiesManagement,
    AuditLogsManagement,
    PatientSummariesManagement
  },
  data() {
    return {
      activeTab: 'users',
      isDeleting: false,
      deleteMessage: '',
      deleteMessageType: ''
    }
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated']
    },
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    },
    currentUserId() {
      const user = this.$store.getters['auth/currentUser']
      return user ? user.id : null
    }
  },
  mounted() {
    this.checkAdminAccess()
  },
  watch: {},

  methods: {
    checkAdminAccess() {
      if (!this.isAuthenticated) {
        this.$router.push(ROUTES.HOME)
        return
      }
      if (!this.isAdmin) {
        // User is authenticated but not an admin
        this.$router.push(ROUTES.HOME)
        return
      }
    },
    async deleteConversations() {
      if (!this.isAuthenticated || !this.isAdmin) {
        this.$router.push(ROUTES.HOME)
        return
      }

      if (!confirm('Are you sure you want to delete all your conversation history? This action cannot be undone.')) {
        return
      }

      this.isDeleting = true
      this.deleteMessage = ''
      this.deleteMessageType = ''

      try {
        const response = await api.delete(API_ENDPOINTS.CONVERSATION_DELETE)

        this.deleteMessage = response.data.message || 'Conversation history deleted successfully'
        this.deleteMessageType = 'success'

        // Clear message after 5 seconds
        setTimeout(() => {
          this.deleteMessage = ''
        }, 5000)
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }

        this.deleteMessage = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
        this.deleteMessageType = 'error'
      } finally {
        this.isDeleting = false
      }
    },
    onStudyImported() {
      // Refresh studies list when a study is imported from ClinicalTrials.gov
      if (this.$refs.studiesManagement) {
        this.$refs.studiesManagement.loadStudies()
      }
    }
  }
}
</script>

<style scoped>
.admin-container {
  min-height: 100vh;
  background-color: #fbf6e9;
  padding: 2rem 1rem;
}

.admin-content {
  width: 80%;
  max-width: 100%;
  margin: 0 auto;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.admin-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2rem;
}

.tabs-container {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e2e8f0;
}

.tab-button {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: #64748b;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: -2px;
}

.tab-button:hover {
  color: #3b82f6;
}

.tab-button.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
}

.admin-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.refresh-btn {
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.section-description {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.delete-btn {
  background-color: #dc2626;
  color: #ffffff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-btn:hover:not(:disabled) {
  background-color: #b91c1c;
}

.delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.message {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
}

.message.success {
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid #10b981;
}

.message.error {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #ef4444;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}
</style>


